import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { DURATION_FAST, EASE_OUT } from '@/lib/motion';
import { cn } from '@/lib/utils';

// --- Date helpers -----------------------------------------------------------

function parseIso(iso: string): { y: number; m: number; d: number } | null {
  if (!iso) return null;
  const parts = iso.split('-').map(Number);
  if (parts.length !== 3 || parts.some(isNaN)) return null;
  return { y: parts[0], m: parts[1], d: parts[2] };
}

function toIso(y: number, m: number, d: number): string {
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function todayIso(): string {
  const n = new Date();
  return toIso(n.getFullYear(), n.getMonth() + 1, n.getDate());
}

function displayDate(iso: string): string {
  const p = parseIso(iso);
  if (!p) return '';
  return new Date(p.y, p.m - 1, p.d).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

const DOW = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

// ---------------------------------------------------------------------------

interface DatePickerProps {
  id?: string;
  label: string;
  value: string;        // YYYY-MM-DD or ''
  onChange: (v: string) => void;
  min?: string;         // YYYY-MM-DD
  max?: string;         // YYYY-MM-DD
  placeholder?: string;
}

const DatePicker: React.FC<DatePickerProps> = ({
  id,
  label,
  value,
  onChange,
  min,
  max,
  placeholder = 'Pick a date',
}) => {
  const today = todayIso();
  const parsed = parseIso(value);
  const now = new Date();

  const [open, setOpen] = useState(false);
  const [viewY, setViewY] = useState(parsed?.y ?? now.getFullYear());
  const [viewM, setViewM] = useState(parsed?.m ?? now.getMonth() + 1);
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({});

  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const approxPanelH = 320;
    const top = spaceBelow >= approxPanelH ? rect.bottom + 4 : rect.top - approxPanelH - 4;
    setPanelStyle({ position: 'fixed', top, left: rect.left, zIndex: 9999 });
  }, []);

  const handleOpen = () => {
    const target = parsed ?? parseIso(today)!;
    setViewY(target.y);
    setViewM(target.m);
    updatePosition();
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleSelect = (iso: string) => {
    onChange(iso);
    handleClose();
    triggerRef.current?.focus();
  };

  // Outside mousedown
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!triggerRef.current?.contains(t) && !panelRef.current?.contains(t)) handleClose();
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  // Scroll repositions panel instead of closing — mirrors SearchableSelect behaviour
  useEffect(() => {
    if (!open) return;
    const h = () => handleClose();
    window.addEventListener('scroll', h, true);
    return () => window.removeEventListener('scroll', h, true);
  }, [open]);

  // Escape
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { handleClose(); triggerRef.current?.focus(); }
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [open]);

  // --- Calendar grid --------------------------------------------------------

  const firstDay = new Date(viewY, viewM - 1, 1);
  const daysInMonth = new Date(viewY, viewM, 0).getDate();
  // Monday-based week: Sun (0) → position 6
  let startOffset = firstDay.getDay() - 1;
  if (startOffset < 0) startOffset = 6;

  const monthLabel = firstDay.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const prevMonth = () => {
    if (viewM === 1) { setViewY(y => y - 1); setViewM(12); }
    else setViewM(m => m - 1);
  };
  const nextMonth = () => {
    if (viewM === 12) { setViewY(y => y + 1); setViewM(1); }
    else setViewM(m => m + 1);
  };

  const isDisabledDay = (d: number) => {
    const iso = toIso(viewY, viewM, d);
    return Boolean((min && iso < min) || (max && iso > max));
  };

  const todayOutOfRange = Boolean((min && today < min) || (max && today > max));

  const cells: Array<number | null> = [
    ...Array(startOffset).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  // --------------------------------------------------------------------------

  return (
    <>
      <div className="flex flex-col gap-1.5">
        <label
          htmlFor={id}
          className="text-xs font-medium text-text-secondary cursor-pointer"
          onClick={handleOpen}
        >
          {label}
        </label>
        <button
          ref={triggerRef}
          id={id}
          type="button"
          onClick={open ? handleClose : handleOpen}
          aria-expanded={open}
          aria-haspopup="dialog"
          className={cn(
            'flex items-center gap-2 px-3 py-2.5 min-w-[144px]',
            'border border-[rgba(189,188,201,0.4)] rounded-full bg-background-primary text-sm text-left',
            'hover:border-border-primary/60 transition-colors duration-[120ms]',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-200 focus-visible:border-transparent',
          )}
        >
          <CalendarDaysIcon className="w-4 h-4 text-text-tertiary shrink-0" />
          <span className={value ? 'text-text-primary' : 'text-text-secondary'}>
            {value ? displayDate(value) : placeholder}
          </span>
        </button>
      </div>

      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              ref={panelRef}
              role="dialog"
              aria-label={`${label} calendar`}
              style={{ ...panelStyle, width: 280, transformOrigin: 'top left' }}
              initial={{ opacity: 0, scale: 0.96, y: -4 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: -4 }}
              transition={{ duration: DURATION_FAST + 0.03, ease: EASE_OUT }}
              className="bg-background-primary border border-[rgba(189,188,201,0.4)] rounded-2xl shadow-2xl p-4"
            >
              {/* Month / year navigation */}
              <div className="flex items-center justify-between mb-4">
                <button
                  type="button"
                  onClick={prevMonth}
                  aria-label="Previous month"
                  className="p-1.5 rounded-full hover:bg-action-hover text-text-secondary transition-colors"
                >
                  <ChevronLeftIcon className="w-4 h-4" />
                </button>
                <span className="text-sm font-semibold text-text-primary select-none">
                  {monthLabel}
                </span>
                <button
                  type="button"
                  onClick={nextMonth}
                  aria-label="Next month"
                  className="p-1.5 rounded-full hover:bg-action-hover text-text-secondary transition-colors"
                >
                  <ChevronRightIcon className="w-4 h-4" />
                </button>
              </div>

              {/* Day-of-week header row */}
              <div className="grid grid-cols-7 mb-1">
                {DOW.map(d => (
                  <div
                    key={d}
                    className="text-center text-[10px] font-bold text-text-tertiary py-1 uppercase tracking-wide"
                  >
                    {d}
                  </div>
                ))}
              </div>

              {/* Day cells */}
              <div className="grid grid-cols-7 gap-y-0.5">
                {cells.map((day, i) => {
                  if (day === null) return <div key={`empty-${i}`} />;
                  const iso = toIso(viewY, viewM, day);
                  const disabled = isDisabledDay(day);
                  const selected = value === iso;
                  const isToday = today === iso;

                  return (
                    <button
                      key={day}
                      type="button"
                      disabled={disabled}
                      onClick={() => handleSelect(iso)}
                      className={cn(
                        'h-8 w-full rounded-full text-sm transition-colors duration-[120ms] text-center',
                        selected && 'bg-primary-500 text-primary-ink font-medium shadow-sm',
                        !selected && isToday && [
                          'text-primary-600 dark:text-primary-350 font-semibold',
                          'ring-1 ring-inset ring-primary-400/40',
                        ],
                        !selected && !isToday && !disabled && 'text-text-primary hover:bg-action-hover',
                        disabled && 'text-text-disabled opacity-40 cursor-not-allowed',
                      )}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>

              {/* Footer shortcuts */}
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-border-primary/10">
                <button
                  type="button"
                  onClick={() => { onChange(''); handleClose(); }}
                  className="text-xs text-text-tertiary hover:text-text-secondary transition-colors"
                >
                  Clear
                </button>
                <button
                  type="button"
                  disabled={todayOutOfRange}
                  onClick={() => handleSelect(today)}
                  className="text-xs font-medium text-primary-600 dark:text-primary-350 hover:text-primary-700 dark:hover:text-primary-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Today
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export default DatePicker;
